import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Loader2, Rocket } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Five-step onboarding checklist (Phase 7 §A5). Hides itself once every
 * step is `done`. Polls `/api/onboarding/status` every 30 s so the card
 * picks up changes the user makes in another tab.
 */
export default function OnboardingChecklist() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancel = false;
    const fetchOnce = async () => {
      try {
        const r = await api.get("/onboarding/status");
        if (!cancel) setStatus(r.data);
      } catch (err) {
        if (!cancel) toastError(err, "Failed to load onboarding");
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 30000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, []);

  if (!status) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-5 flex items-center text-sm text-muted-foreground"
        data-testid="onboarding-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking setup…
      </div>
    );
  }
  if (status.complete || dismissed) return null;

  const doneCount = status.steps.filter((s) => s.done).length;
  return (
    <div
      className="rounded-lg border border-border bg-card overflow-hidden"
      data-testid="onboarding-card"
    >
      <div className="flex items-start gap-3 border-b border-border bg-secondary/40 px-5 py-4">
        <Rocket className="h-5 w-5 text-primary mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold tracking-tight">
            Finish setting up VoxIVR
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {doneCount} of {status.steps.length} done — complete the
            remaining steps to start handling calls.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDismissed(true)}
          data-testid="onboarding-dismiss"
        >
          Hide
        </Button>
      </div>
      <ul className="divide-y divide-border">
        {status.steps.map((s) => (
          <li
            key={s.id}
            data-testid={`onboarding-step-${s.id}`}
            className="flex items-center gap-3 px-5 py-3"
          >
            <span
              className={
                "flex h-6 w-6 items-center justify-center rounded-full text-xs " +
                (s.done
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                  : "bg-secondary text-muted-foreground")
              }
            >
              {s.done ? <Check className="h-3.5 w-3.5" /> : "·"}
            </span>
            <span
              className={
                "flex-1 text-sm " +
                (s.done ? "text-muted-foreground line-through" : "")
              }
            >
              {s.label}
            </span>
            {!s.done && (
              <Button asChild size="sm" variant="outline">
                <Link to={s.link} data-testid={`onboarding-go-${s.id}`}>
                  Go <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
